import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import decode from "jwt-decode";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { environment } from "./../../../../environments/environment";
import { User } from "./../../../models/users/user.model";
import { Authentication } from "./../models/authentication.model";
import { Credentials } from "./../models/credentials";

const ACCESS_TOKEN = "access_token";
const REFRESH_TOKEN = "refresh_token";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  constructor(public http: HttpClient, public router: Router) {}

  login(credentials: Credentials): Observable<Authentication> {
    const body = new HttpParams()
      .set("grant_type", "password")
      .set("username", credentials.username)
      .set("password", credentials.password);
    const headers = new HttpHeaders()
      .set("Authorization", "Basic " + environment.api_key)
      .set("Content-Type", "application/x-www-form-urlencoded");
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers,
      })
      .pipe(
        map((response: Authentication) => {
          localStorage.removeItem(ACCESS_TOKEN);
          localStorage.removeItem(REFRESH_TOKEN);
          return response;
        })
      );
  }

  refreshToken(): Observable<Authentication> {
    const token = this.getToken(REFRESH_TOKEN);
    const body = new HttpParams()
      .set("grant_type", REFRESH_TOKEN)
      .set("refresh_token", token);
    const headers = new HttpHeaders()
      .set("Authorization", "Basic " + environment.api_key)
      .set("Content-Type", "application/x-www-form-urlencoded");
    return this.http
      .post(`${environment.api_url}/oauth/token`, body, {
        headers,
      })
      .pipe(
        map((response: Authentication) => {
          return response;
        })
      );
  }

  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN);
    localStorage.removeItem(REFRESH_TOKEN);
    this.router.navigate(["/login"], { replaceUrl: true });
  }

  setSession(access_token: string, refresh_token: string): void {
    localStorage.setItem(ACCESS_TOKEN, access_token);
    localStorage.setItem(REFRESH_TOKEN, refresh_token);
  }

  getToken(type: string = ACCESS_TOKEN): string {
    return localStorage.getItem(type);
  }

  isAuthenticated(): boolean {
    const token = this.getToken();

    if (token) {
      return !this.isTokenExpired();
    } else {
      return false;
    }
  }

  private decodeToken(type: string = ACCESS_TOKEN): any {
    const token = this.getToken(type);
    if (!token) return {};
    try {
      return decode(token);
    } catch {
      return {};
    }
  }

  getTokenExpirationDate(token: string): Date {
    if (!token) return null;
    let decoded: any;
    try {
      decoded = decode(token);
    } catch {
      return null;
    }

    if (decoded.exp === undefined) return null;

    const date = new Date(0);
    date.setUTCSeconds(decoded.exp);
    return date;
  }

  isTokenExpired(type: string = ACCESS_TOKEN): boolean {
    const token = this.getToken(type);
    if (!token) return true;

    const date = this.getTokenExpirationDate(token);
    if (!date) return true;
    return !(date.valueOf() > new Date().valueOf());
  }

  hasRole(role: string): boolean {
    const roleClaim = this.decodeToken().role;
    return roleClaim === role;
  }

  hasPrivilege(privilege: string): boolean {
    const privilegesClaim: string[] = this.decodeToken().authorities;
    return (
      privilegesClaim && privilegesClaim.findIndex((x) => x === privilege) > -1
    );
  }

  isAuthenticatedOrRefresTokenNotExpired() {
    if (this.isAuthenticated() || !this.isTokenExpired(REFRESH_TOKEN)) {
      return true;
    } else if (
      window.location.pathname !== "/login" &&
      window.location.pathname !== "/"
    ) {
      this.logout();
      return false;
    }
    return false;
  }

  isCurrentUser(user: User): boolean {
    const email = this.decodeToken().user_name;
    return email === user.email;
  }

  getCurrentUserEmail(): string {
    return this.decodeToken().user_name;
  }

  getCurrentUserId(): string {
    return this.decodeToken().user_id;
  }
}
